import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { api } from '@/lib/api';
import { ticketKeys } from './keys';
import { isStale } from '@/types/investigation';
import type { Investigation } from '@/types/investigation';
import type { AiSettings } from './settings';

export type TriageDepth = 'reply' | 'light' | 'deep';

export interface TriageUsage {
  inputTokens: number;
  outputTokens: number;
  estimatedCost: number | null;
}

export interface Triage {
  depth: TriageDepth;
  reasoning: string;
  plan: string[];
  skipped: boolean;
  durationMs: number;
  usage: TriageUsage | null;
}

// The snapshot's tiers/triage are the same server object as AiSettings, just without the
// top-level `enabled`/`updatedBy`/`updatedAt` — reuse its shapes rather than a looser duplicate.
export type AiSettingsSnapshot = Pick<AiSettings, 'triage' | 'tiers'>;

// `triage` and `requestedDepth` came in with the triage rollout and are present on every doc
// (list and detail responses alike); `config` is a settings snapshot only the detail endpoint
// returns. The shared Investigation type predates all three, so they're layered on here rather
// than widening that type for every other consumer.
export type InvestigationWithTriage = Investigation & {
  triage: Triage | null;
  requestedDepth: 'auto' | 'deep';
};

export type InvestigationDetail = InvestigationWithTriage & {
  config?: AiSettingsSnapshot;
};

export interface InvestigationListResponse {
  data: InvestigationWithTriage[];
}

// Global QueryClient default staleTime is 30s (main.tsx) — polling hooks need
// their own runs judged fresh on every tick, so they override it to 0.
export function useInvestigations(ticketId: string) {
  return useQuery({
    queryKey: ticketKeys.investigations(ticketId),
    queryFn: () => api<InvestigationListResponse>(`/support-tickets/${ticketId}/investigations`),
    staleTime: 0,
    refetchInterval: query => {
      const runs = query.state.data?.data ?? [];
      const active = runs.some(r => (r.status === 'queued' || r.status === 'running') && !isStale(r));
      return active ? 3000 : false;
    }
  });
}

// List responses project debug out, so the live step feed for the active run
// polls this detail endpoint instead. Debug views on terminal runs (completed/
// failed/cancelled) reuse this same query but pass poll:false — the record is
// static once the run has stopped, so there's nothing to refetch every 3s.
export function useInvestigation(ticketId: string, investigationId: string, enabled: boolean, poll = true) {
  return useQuery({
    queryKey: ticketKeys.investigation(ticketId, investigationId),
    queryFn: () => api<InvestigationDetail>(`/support-tickets/${ticketId}/investigations/${investigationId}`),
    enabled: enabled && Boolean(investigationId),
    staleTime: 0,
    refetchInterval: poll ? 3000 : false
  });
}

export function useStartInvestigation(ticketId: string) {
  const qc = useQueryClient();
  // TVariables is explicitly `| void`, not `| undefined` — only `void` lets callers omit the
  // argument entirely (existing plain re-runs), while still accepting `{ forceDepth: 'deep' }`.
  return useMutation<{ investigationId: string }, Error, { forceDepth?: 'deep' } | void>({
    mutationFn: args =>
      api<{ investigationId: string }>(`/support-tickets/${ticketId}/investigate`, {
        method: 'POST',
        body: args?.forceDepth ? { forceDepth: args.forceDepth } : undefined
      }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ticketKeys.investigations(ticketId) }),
    onError: err => {
      const status = (err as { status?: number; statusCode?: number } | null)?.status ?? (err as { statusCode?: number } | null)?.statusCode;
      // A 409 also means the API has just swept any stale runs for this ticket, so refetch — the
      // list the panel is showing may already be out of date.
      if (status === 409) {
        toast.error('An investigation is already running');
        qc.invalidateQueries({ queryKey: ticketKeys.investigations(ticketId) });
      } else if (status === 404) toast.error('AI Investigate is not enabled');
      else toast.error((err as Error).message ?? 'Could not start investigation');
    }
  });
}
